import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { Heading, HeadingHierarchy } from '../../../_lib/components/Heading/Heading';
import { PostsEditor } from '../../../_lib/components/Posts';
import { useBlockContext, useFocusOnRefreshWithTabIndex, useRefreshBlock } from '../../../_lib/BlockFetcher';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { BlockType } from '../../../types/api/ElementType';
import { SonForm } from '../../../_lib/Form2';
import { PostBlockTypes, PostFormTypes, postType } from '../../../_lib/components/Posts/Post.types';
import { Help } from '../../../_lib/Form2/partials/Help/Help';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';

type CommentsStandardEditorProps = {
  subHeadingsHierarchy: HeadingHierarchy;
  createForm: SonForm<PostFormTypes>;
};

export function CommentsMainEditor({ subHeadingsHierarchy, createForm }: CommentsStandardEditorProps) {
  // some forms don't open editor, so we need a separate state to check
  const [toggleEditor, setToggleEditor] = useState<boolean>(false);
  const { refreshCount, refreshProps } = useRefreshBlock();
  const focusRef = useFocusOnRefreshWithTabIndex<HTMLHeadingElement>(refreshCount);
  const { hash } = useLocation();
  const { uuid, elementType } = useBlockContext();
  const { t } = useSonTranslation(elementType === BlockType.comments ? 'CommentsBlock' : 'EditorFeedback');
  const commentsMainEditorId = 'comments-main-editor-' + uuid;

  useEffect(() => {
    if (!hash.includes(commentsMainEditorId)) {
      setToggleEditor(false);
    }
  }, [hash]);

  useEffect(() => {
    setToggleEditor(false);
  }, [refreshCount]);

  return (
    <>
      <div className="posts__main-heading-container">
        <Heading
          hierarchy={subHeadingsHierarchy}
          className="posts__heading font-bold-15-19"
          ref={refreshProps === undefined ? focusRef : null}
        >
          {t('default.heading.writeComment')}
        </Heading>
        {elementType === BlockType.editorFeedback
          ? (
              // @ts-expect-error compiler can not distinguish which translation is used here
              <Help id={`comments-help-${uuid}`} helpText={t('edit.help')} tooltipPlacement={TooltipPlacement.TOP} />
            )
          : null}
      </div>
      {!toggleEditor && (
        <a
          className="posts__fake-textarea"
          href={`#${commentsMainEditorId}`}
          onClick={() => setToggleEditor(!toggleEditor)}
        >
          {t('default.textareaPlaceholder')}
        </a>
      )}
      <div className="posts__skip-to-editor" id={commentsMainEditorId}>
        <div className="posts__skip-to-editor-content">
          {toggleEditor &&
            <PostsEditor form={createForm} type={postType(elementType as PostBlockTypes)} autoFocus uuid={uuid} />}
        </div>
      </div>
    </>
  );
}
